import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ReactMarkdown from "react-markdown";
import { getButtonAppearance } from "utils/button";
import * as z from "zod";

import Button from "../elements/button";

import styles from "@/styles/contact.module.scss";

const ContactForm = ({ data }) => {
  const bg =
    data.contactBackground === "white"
      ? "bg-white"
      : data.contactBackground === "grey"
        ? "bg-onmoLightGrey"
        : "bg-onmoBeige";
  const [formSent, setFormSent] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("onmo-pro") === "sent") {
      setFormSent(true);
    } else {
      setFormSent(false);
    }
  }, []);

  const schema = z.object({
    firstname: z.string().min(1, "Please check your first name"),
    lastname: z.string().min(1, "Please check your last name"),
    email: z
      .string()
      .email("Please enter a valid email address")
      .min(1, "Please enter a valid email address"),
    company: z.string().min(1, "Please check your company name"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
  });

  const Input = ({ name, onChange, type, registerName, error }) => {
    return (
      <div className="flex flex-col w-full gap-2 w-full md:max-w-[400px] mx-auto">
        <label htmlFor={registerName} className={styles.label}>
          {name}
        </label>
        <input
          id={registerName}
          {...register(registerName)}
          className={!error ? styles.input : styles.inputError}
          type={type}
          onChange={onChange}
        />
        {error ? (
          Object.keys(errors).length === 4 ? null : (
            <p className="text-onmoRed">{error}</p>
          )
        ) : null}
      </div>
    );
  };

  async function onSubmitHandler(data) {
    // eslint-disable-next-line no-undef
    grecaptcha.ready(async function () {
      // eslint-disable-next-line no-undef
      grecaptcha
        .execute("6LdUF8caAAAAAI-1Z2LKO8-7VmEOP5H6xI_McvSS", {
          action: "submit",
        })
        .then(async function (token) {
          const body = {
            data: {
              token: token,
              getEmail: data.email,
              getFirstName: data.firstname,
              getLastName: data.lastname,
              getCompanyName: data.company,
            },
          };
          await axios
            .post(`${process.env.NEXT_PUBLIC_ONMO_PRO_FORM}`, body)
            .then(res => {
              setFormSent(true);
              sessionStorage.setItem("onmo-pro", "sent");
              reset();
            })
            .catch(err => {
              setFormSent(true);
              sessionStorage.setItem("onmo-pro", "sent");
              reset();
            });
        });
    });
  }

  return (
    <section className={`${bg} px-5 py-10 md:p-20`}>
      <div className="flex flex-col gap-10 max-w-[450px] mx-auto">
        {formSent ? (
          <div className="text-center flex flex-col gap-10 items-center justify-center py-24">
            <svg
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.5 15L16.5 21L25.5 30L31.5 6L4.5 16.5L10.5 19.5L13.5 28.5L18 22.5"
                stroke="#232323"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h1 className={styles.thanks}>Thanks!</h1>
            <p className={styles.message}>
              Your details have now been added to our waiting list.
            </p>
          </div>
        ) : (
          <form
            className="flex flex-col gap-5 md:gap-10"
            onSubmit={handleSubmit(onSubmitHandler)}
          >
            <div className="w-full md:max-w-[400px] mx-auto">
              <div className="relative">
                {Object.keys(errors).length === 4 ? (
                  <p className="text-onmoRed absolute top-[-50px] w-[400px] mx-auto">
                    Please enter your details
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Input
                  name="First name"
                  type="text"
                  registerName="firstname"
                  error={errors.firstname?.message}
                />
                <Input
                  name="Last name"
                  type="text"
                  registerName="lastname"
                  error={errors.lastname?.message}
                />
              </div>
            </div>
            <Input
              name="Company name"
              type="text"
              registerName="company"
              error={errors.company?.message}
            />
            <Input
              name="Email address"
              type="email"
              registerName="email"
              error={errors.email?.message}
            />
            <ReactMarkdown
              className={`text-left md:text-center mx-auto ${styles.contactFormContent}`}
            >
              {data.content}
            </ReactMarkdown>
            <div className="flex justify-center">
              <Button
                button={data.formButton}
                appearance={getButtonAppearance(data.formButton.type)}
                size="medium"
              />
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default ContactForm;
